import { useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { DailyQuestAssignAndGetResponse, DailyQuestWithProgress } from '../../../models/apiTypes';
import type { DailyQuestView } from '../../../models/quest';
import { isAdQuest, getAllQuests, sortQuests } from '../../../utils/quest/questHelpers';
import { CLAIMED_QUEST_PROTECTION_MS, globalClaimedQuestTimestamps } from '../constants';
import { isRewardClaimed } from '../../../utils/quest/optimisticQuestState';
import { getQuestsData } from '../questCatalog';
import { getProgressText } from '../questProgress';

export interface UseDailyQuestListParams {
  hourlyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  marketingQuestsData: DailyQuestAssignAndGetResponse | undefined;
  dailyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  mainQuestsData: DailyQuestAssignAndGetResponse | undefined;
  repeatableQuestsData: DailyQuestAssignAndGetResponse | undefined;
  t: TFunction;
  i18nLanguage: string;
  claimedQuestIds: Set<number>;
  watchedAdQuestIds: Set<number>;
  adQuestProgress: Map<number, number>;
  isOpen: boolean;
  sortedQuests: DailyQuestView[];
  setSortedQuests: Dispatch<SetStateAction<DailyQuestView[]>>;
}

export interface UseDailyQuestListResult {
  questsWithoutSorting: DailyQuestView[];
  questsWithoutSortingRef: { current: DailyQuestView[] };
  quest41State: {
    userQuestId: number | null | undefined;
    isFinished: boolean;
    isRewardClaimed: boolean;
    hasActiveProtection: boolean;
  } | null;
  quests: DailyQuestView[];
}

export function useDailyQuestList({
  hourlyQuestsData,
  marketingQuestsData,
  dailyQuestsData,
  mainQuestsData,
  repeatableQuestsData,
  t,
  i18nLanguage,
  claimedQuestIds,
  watchedAdQuestIds,
  adQuestProgress,
  isOpen,
  sortedQuests,
  setSortedQuests,
}: UseDailyQuestListParams): UseDailyQuestListResult {
  // Обчислюємо квести (без сортування за isFinished)
  const questsWithoutSorting = useMemo(() => {
    // Об'єднуємо hourly, маркетингові, щоденні та main квести
    const allQuests = getAllQuests(
      hourlyQuestsData?.quests,
      dailyQuestsData?.quests,
      marketingQuestsData?.quests,
      mainQuestsData?.quests,
      repeatableQuestsData?.quests
    );
    
    if (allQuests.length === 0) return [];
    
    const mappedQuests = allQuests.map((quest: DailyQuestWithProgress) => {
      const questsData = getQuestsData({ t });
      const questData = questsData.find((c) => c.questId === quest.quest_id) || {
        title: `Quest ${quest.quest_id}`,
        description: "",
        rewardAmount: 0,
        rewardType: "exp" as const,
        hasLink: false,
        secondaryRewardAmount: 0,
        secondaryRewardType: "currency" as const,
      };
      const progress = getProgressText({ quest, watchedAdQuestIds, t });
      
      // Для квестів з рекламою: якщо реклама була переглянута локально, вважаємо квест завершеним
      const questIsAdQuest = isAdQuest(quest.quest_id, quest.objectives?.[0]?.type);
      const wasAdWatched = questIsAdQuest && quest.user_quest_id && watchedAdQuestIds.has(quest.user_quest_id); // quest тут з API, до маппінгу
      const adProgress = quest.user_quest_id ? adQuestProgress.get(quest.user_quest_id) : undefined; // quest тут з API, до маппінгу
      // hasAdProgress має бути true якщо є progress (включаючи 0), але менше 100
      const hasAdProgress = questIsAdQuest && adProgress !== undefined && adProgress >= 0 && adProgress < 100;
      
      return {
        questId: quest.quest_id,
        userQuestId: quest.user_quest_id,
        objectives: quest.objectives,
        title: questData.title,
        description: questData.description,
        rewardAmount: questData.rewardAmount,
        rewardType: questData.rewardType,
        // Catalog dual-reward fields (optional on QuestCatalogEntry; not on API Quest type).
        secondaryRewardAmount: questData.secondaryRewardAmount || 0,
        secondaryRewardType: questData.secondaryRewardType || "currency",
        hasLink: questData.hasLink,
        progressText: progress.text,
        progressLabel: progress.progressLabel,
        progressTaskDescription: progress.taskDescription,
        progressTaskImages: progress.taskImages,
        progressCompleted: progress.isCompleted,
        // Для квестів з рекламою: якщо реклама переглянута локально (після прогрес-бару), вважаємо завершеним
        isFinished: wasAdWatched || quest.is_finished,
        // Прогрес для квестів з рекламою (0-100, undefined якщо немає)
        adProgress: hasAdProgress ? adProgress : undefined,
        // Використовуємо reward_claimed з бекенду, стан з claimedQuestIds, або глобальний захист
        // One durable source of truth: survives a modal remount, a cache that
        // has not been rewritten yet, and the 10s window expiring.
        isRewardClaimed: isRewardClaimed(quest.user_quest_id, quest.reward_claimed, claimedQuestIds),
        isMarketing: (marketingQuestsData?.quests || []).some(q => q.user_quest_id === quest.user_quest_id), // quest тут з API, до маппінгу
        isRepeatable: (repeatableQuestsData?.quests || []).some(q => q.user_quest_id === quest.user_quest_id),
        minRequirements: quest.min_requirements,
      };
    });

    // Сортуємо квести: активні зверху, завершені з забраною нагородою — знизу
    return sortQuests(mappedQuests);
    // i18nLanguage навмисно в deps: t інколи зберігає identity при зміні мови,
    // тож language code бустить memo, щоб підписи квестів перезібрались.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nLanguage intentional bust
  }, [hourlyQuestsData, marketingQuestsData, dailyQuestsData, mainQuestsData, repeatableQuestsData, t, i18nLanguage, claimedQuestIds, watchedAdQuestIds, adQuestProgress]);

  // Зберігаємо попередній adProgress для порівняння (щоб не пересортовувати при зміні claimedQuestIds)
  const prevAdProgressRef = useRef<Map<number, number>>(new Map());

  // Стоїть в true рівно на один ефект-проліт одразу після фіксації порядку
  // нижче — див. коментар біля ефекту, що читає sortedQuestsRef, для причини.
  const justFrozenRef = useRef(false);

  // При відкритті модалки — фіксуємо порядок з questsWithoutSorting (вже відсортований у sortQuests)
  useEffect(() => {
    if (isOpen && questsWithoutSorting.length > 0) {
      setSortedQuests([...questsWithoutSorting]);
      justFrozenRef.current = true;

      // Зберігаємо поточний adProgress для порівняння
      const currentAdProgress = new Map<number, number>();
      questsWithoutSorting.forEach((q) => {
        if (q.adProgress !== undefined && q.userQuestId) {
          currentAdProgress.set(q.userQuestId, q.adProgress);
        }
      });
      prevAdProgressRef.current = currentAdProgress;
    } else if (!isOpen) {
      setSortedQuests([]);
      prevAdProgressRef.current = new Map();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/close only; omit questsWithoutSorting to freeze order
  }, [isOpen]); // Фіксуємо порядок тільки при відкритті/закритті (questsWithoutSorting НЕ в залежностях)

  // Використовуємо refs для збереження актуальних значень, щоб уникнути проблем з замиканням
  const sortedQuestsRef = useRef(sortedQuests);
  const questsWithoutSortingRef = useRef(questsWithoutSorting);

  // Стан квесту 41 (Task Ad) для передачі в AdsgramTaskQuest
  const quest41State = useMemo(() => {
    const q = questsWithoutSorting.find(q => q.questId === 41);
    if (!q) return null;
    const hasProtection = !!(q.userQuestId != null && globalClaimedQuestTimestamps.has(q.userQuestId) &&
      // eslint-disable-next-line react-hooks/purity -- claim-protection time window, intentional time read
      Date.now() - (globalClaimedQuestTimestamps.get(q.userQuestId) || 0) < CLAIMED_QUEST_PROTECTION_MS);
    return {
      userQuestId: q.userQuestId,
      isFinished: !!q.isFinished,
      isRewardClaimed: !!q.isRewardClaimed,
      hasActiveProtection: hasProtection,
    };
  }, [questsWithoutSorting]);

  // Оновлюємо refs при зміні
  useEffect(() => {
    sortedQuestsRef.current = sortedQuests;
  }, [sortedQuests]);

  useEffect(() => {
    questsWithoutSortingRef.current = questsWithoutSorting;
  }, [questsWithoutSorting]);
  
  // Оновлюємо sortedQuests для progress bar (без пересортування) коли questsWithoutSorting змінюється через adProgress або watchedAdQuestIds
  // Використовуємо useMemo для створення рядка-залежності з adProgress та watchedAdQuestIds
  const adProgressString = useMemo(() => {
    return JSON.stringify(Array.from(adQuestProgress.entries()));
  }, [adQuestProgress]);
  
  const watchedAdQuestIdsString = useMemo(() => {
    return JSON.stringify(Array.from(watchedAdQuestIds).sort());
  }, [watchedAdQuestIds]);
  
  // Створюємо рядок-залежність для questsWithoutSorting, щоб відстежувати зміни даних з бекенду (is_finished, reward_claimed тощо)
  const questsWithoutSortingString = useMemo(() => {
    return JSON.stringify(questsWithoutSorting.map(q => ({
      userQuestId: q.userQuestId,
      isFinished: q.isFinished,
      isRewardClaimed: q.isRewardClaimed,
      progressText: q.progressText,
      progressLabel: q.progressLabel,
      adProgress: q.adProgress,
    })));
  }, [questsWithoutSorting]);
  
  useEffect(() => {
    if (!isOpen) return;

    // The freeze-on-open effect above just called setSortedQuests(fresh,
    // correctly-sorted array) in THIS SAME commit — but sortedQuestsRef.current
    // (synced by its own [sortedQuests] effect) still holds the array from
    // BEFORE that update, since effects run in declaration order within one
    // commit and the ref-sync effect for the freeze's new value hasn't run
    // yet. If this effect proceeded anyway, it would rebuild sortedQuests from
    // that stale ref (patching in fresh fields but keeping the OLD position)
    // and clobber the freeze's correct order with setSortedQuests(updatedQuests)
    // right after — reproduced live: a finished+claimed quest 41 stayed pinned
    // at its pre-claim position on every reopen, even though its own card
    // already showed the correct claimed state (that came from the separate,
    // always-fresh quest41State memo, not from this array). Skipping this pass
    // once, right after a freeze, lets the ref-sync effect catch up first.
    if (justFrozenRef.current) {
      justFrozenRef.current = false;
      return;
    }

    const currentSortedQuests = sortedQuestsRef.current;
    const currentQuestsWithoutSorting = questsWithoutSortingRef.current;
    
    if (currentSortedQuests.length === 0 || currentQuestsWithoutSorting.length === 0) return;
    
    // Створюємо Map для швидкого пошуку актуальних даних
    const questsMap = new Map(currentQuestsWithoutSorting.map(q => [q.userQuestId, q]));
    
    // Оновлюємо sortedQuests з актуальними даними з questsWithoutSorting (без пересортування)
    const updatedQuests = currentSortedQuests.map((sortedQuest) => {
      const updatedQuest = questsMap.get(sortedQuest.userQuestId);
      if (updatedQuest) {
        // Перевіряємо, чи змінилися важливі поля (adProgress, isFinished, isRewardClaimed, progressText)
        if (
          updatedQuest.adProgress !== sortedQuest.adProgress ||
          updatedQuest.isFinished !== sortedQuest.isFinished ||
          updatedQuest.isRewardClaimed !== sortedQuest.isRewardClaimed ||
          updatedQuest.progressText !== sortedQuest.progressText ||
          updatedQuest.progressLabel !== sortedQuest.progressLabel
        ) {
          // Оновлюємо тільки змінені поля, зберігаючи порядок
          return { ...sortedQuest, ...updatedQuest };
        }
      }
      return sortedQuest;
    });
    
    // Перевіряємо, чи були зміни перед оновленням стану
    const hasChanges = updatedQuests.some((updated, index) => {
      const old = currentSortedQuests[index];
      return (
        old?.adProgress !== updated.adProgress ||
        old?.isFinished !== updated.isFinished ||
        old?.isRewardClaimed !== updated.isRewardClaimed ||
        old?.progressText !== updated.progressText ||
        old?.progressLabel !== updated.progressLabel
      );
    });
    
    if (hasChanges) {
      // Не пересортовуємо — квести залишаються на місці після claim
      setSortedQuests(updatedQuests);
    }
  }, [adProgressString, watchedAdQuestIdsString, questsWithoutSortingString, isOpen, setSortedQuests]);

  // Використовуємо відсортований список для рендерингу
  const quests = sortedQuests;

  return {
    questsWithoutSorting,
    questsWithoutSortingRef,
    quest41State,
    quests,
  };
}
